import { ModalBase, type ModalSize } from '@on3/ui-lib/components/Modal';
import { ModalProvider as RootModalProvider } from '@on3/ui-lib/components/Modal/Provider';
import { trackGoogle } from '@on3/ui-lib/utils/tracking';
import {
  type DashboardModalPropsMap,
  dashboardModals,
  type DashboardModalType,
} from '@public/components/Modals/Dashboard';
import {
  type OfferModalPropsMap,
  offerModals,
  type OfferModalType,
} from '@public/components/Modals/Offers';
import {
  type PlayerModalPropsMap,
  playerModals,
  type PlayerModalType,
} from '@public/components/Modals/Player';
import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type ModalType = PlayerModalType | DashboardModalType | OfferModalType;

export type ModalPropsMap = PlayerModalPropsMap &
  DashboardModalPropsMap &
  OfferModalPropsMap;

interface IModal<T extends ModalType = ModalType> {
  type: T;
  props: ModalPropsMap[T];
  size: ModalSize;
  required: boolean;
}

interface IModalContext {
  showModal: (
    type: ModalType,
    props?: ModalPropsMap[ModalType],
    size?: ModalSize,
    required?: boolean,
  ) => void;
  closeModal: () => void;
  setModal: Dispatch<SetStateAction<IModal | null>>;
}

const ModalContext = createContext<IModalContext>({
  showModal: () => {},
  closeModal: () => {},
  setModal: () => {},
});

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [modal, setModal] = useState<IModal | null>(null);

  const modals = useMemo(
    () => ({
      ...playerModals,
      ...dashboardModals,
      ...offerModals,
    }),
    [],
  );

  // Show modal
  const showModal = useCallback(
    (
      type: ModalType,
      props: ModalPropsMap[ModalType] = {},
      size: ModalSize = 'default',
      required: boolean = false,
    ) => {
      setModal({ type, props, size, required });
    },
    [setModal],
  );

  // Close modal
  const closeModal = useCallback(() => {
    trackGoogle('modal_close', {
      modalType: modal?.type,
    });

    setModal(null);
  }, [modal, setModal]);

  const ModalComponent = modal ? modals[modal.type] : null;

  return (
    <RootModalProvider>
      <ModalContext.Provider
        value={{
          showModal,
          closeModal,
          setModal,
        }}
      >
        {children}
        {modal && ModalComponent && (
          <ModalBase
            close={closeModal}
            required={modal.required}
            show={!!modal}
            size={modal.size}
          >
            <ModalComponent {...(modal.props as any)} />
          </ModalBase>
        )}
      </ModalContext.Provider>
    </RootModalProvider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }

  return context;
};

export const withModal = <P extends object>(
  Component: React.ComponentType<P>,
) => {
  const WrappedComponent = (props: P) => {
    return (
      <ModalProvider>
        <Component {...props} />
      </ModalProvider>
    );
  };

  return WrappedComponent;
};
